import { isEqual } from 'lodash';
import { useEffect } from 'react';
import useSWR from 'swr';
import { create } from 'zustand';
import { ReturnFilterStore } from 'zustand-store';
import { consignmentService, exchangeAndReturnService } from 'services';
import { useAuth } from 'hooks';
import {
  Consignment,
  ReturnConsignment,
  ReturnFilterForm,
  ReturnSearchForm,
} from 'types';
import {
  populateSubmitReturnFilter,
  populateSubmitReturnSearch,
  removeLocalStorageItem,
  setTodayLocalStorage,
  swrKeys,
} from 'helpers';
import {
  LocalStorageKeys,
  ONE_MINUTE_MILLISECONDS,
  STATUSES,
  getDefaultReturnFilter,
  getDefaultReturnSearch,
} from 'commons';

interface Store {
  consignmentsToReturn: Consignment[];
  returnConsignments: ReturnConsignment[];
}

interface Actions {
  setConsignmentsToReturn: (returnConsignments: Consignment[]) => void;
  setReturnConsignments: (returnConsignments: ReturnConsignment[]) => void;
}

const initialState: Store = {
  returnConsignments: [],
  consignmentsToReturn: [],
};

const useStore = create<Store & Actions>((set) => ({
  ...initialState,
  setConsignmentsToReturn: (consignmentsToReturn: Consignment[]) =>
    set(() => ({ consignmentsToReturn })),
  setReturnConsignments: (returnConsignments: ReturnConsignment[]) =>
    set(() => ({ returnConsignments })),
}));

const defaultReturnFilter = getDefaultReturnFilter();

const { DELIVERED, DELIVERED_BY_DRIVER, MANUALLY_DELIVERED } = STATUSES;

export function useGetReturnConsignments() {
  const {
    consignmentsToReturn,
    returnConsignments,
    setConsignmentsToReturn,
    setReturnConsignments,
  } = useStore();

  const {
    filteredStore: storeNames,
    filteredBrand: brandName,
    returnSearch,
    returnFilter,
    setReturnSearch,
    setReturnFilter,
    setFilteredBrand,
    setFilteredStore,
    reset,
  } = ReturnFilterStore.useStore();

  const { selectedStore, user } = useAuth();
  const store = selectedStore?.name || '';
  const brand = selectedStore?.brand || '';
  const { isAdmin } = user;

  let search = returnSearch;
  let filter = returnFilter;

  const today = new Date().toDateString();
  if (returnFilter.filterDate !== today || returnSearch.filterDate !== today) {
    search = getDefaultReturnSearch();
    filter = getDefaultReturnFilter();

    setReturnSearch(search);
    setReturnFilter(filter);
  }

  const submitSearch = populateSubmitReturnSearch(search);
  const submitReturnFilter = populateSubmitReturnFilter(filter);

  const { codes } = submitSearch;
  const onlyCodeFilter = { codes, storeNames };
  const fullFilter = {
    ...submitSearch,
    ...submitReturnFilter,
    storeNames,
  };

  const submitFilter = codes ? onlyCodeFilter : fullFilter;

  const { data, mutate, isValidating, isLoading } = useSWR(
    swrKeys.returnConsignments({ key: !isAdmin ? storeNames : brandName }),
    () => exchangeAndReturnService.get(submitFilter),
    {
      refreshWhenHidden: true,
      revalidateOnFocus: false,
      revalidateOnMount: false,
      refreshInterval: ONE_MINUTE_MILLISECONDS,
    }
  );

  const submitToReturnFilter = {
    ...submitSearch,
    statuses: `${DELIVERED.value},${DELIVERED_BY_DRIVER.value},${MANUALLY_DELIVERED.value}`,
    storeNames,
  };

  const canFetch = Boolean(submitSearch.codes) || Boolean(submitSearch.cpf);

  const {
    data: dataConsignmentsToReturn,
    mutate: mutateConsignmentsToReturn,
    isValidating: isValidatingConsignmentsToReturn,
    isLoading: isLoadingConsignmentsToReturn,
  } = useSWR(
    canFetch
      ? swrKeys.consignmentsToReturn({
          key: !isAdmin ? storeNames : brandName,
        })
      : null,
    () => consignmentService.get(submitToReturnFilter),
    {
      refreshWhenHidden: true,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (storeNames !== store || brandName !== brand) {
      reset({ brand, store });
    }
  }, [store, brand, storeNames, brandName, reset]);

  useEffect(() => {
    const { searchType } = returnSearch;

    if (returnSearch[searchType]) {
      setTodayLocalStorage<ReturnSearchForm>(
        LocalStorageKeys.ReturnSearch,
        returnSearch
      );
    } else {
      removeLocalStorageItem(LocalStorageKeys.ReturnSearch);
    }
  }, [returnSearch]);

  useEffect(() => {
    if (isEqual(returnFilter, defaultReturnFilter)) {
      removeLocalStorageItem(LocalStorageKeys.ReturnFilter);
    } else {
      setTodayLocalStorage<ReturnFilterForm>(
        LocalStorageKeys.ReturnFilter,
        returnFilter
      );
    }
  }, [returnFilter]);

  useEffect(() => {
    if (!data) return;

    setReturnConsignments(data);
  }, [data, setReturnConsignments]);

  useEffect(() => {
    if (!dataConsignmentsToReturn && consignmentsToReturn) {
      setConsignmentsToReturn([]);
      return;
    }

    if (dataConsignmentsToReturn) {
      setConsignmentsToReturn(dataConsignmentsToReturn);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataConsignmentsToReturn, setConsignmentsToReturn]);

  return {
    consignmentsToReturn,
    returnConsignments,
    filteredStore: storeNames,
    filteredBrand: brandName,
    returnSearch,
    returnFilter,
    isLoading,
    isValidating,
    isValidatingConsignmentsToReturn,
    isLoadingConsignmentsToReturn,
    mutateConsignmentsToReturn,
    mutate,
    reset,
    setReturnConsignments,
    setReturnSearch,
    setReturnFilter,
    setFilteredBrand,
    setFilteredStore,
  };
}

export const ReturnConsignmentsListStore = {
  useStore,
  useGetReturnConsignments,
};
